import { Many, uniqBy, ValueIterateeCustom, ValueIteratee } from 'lodash';
import { filter, flow, map, sortBy, uniq } from 'lodash/fp';
import { sortGrade } from 'utilities/grade-util';
import { ProductWithCurriculum } from 'types';
import {
  ClassroomLicenseUsage,
  Maybe,
  Product,
  ProductCurriculum,
  ProductFamily,
  ProductType,
  StudentLicenseUsage,
  UserLicenseUsage,
  OrganizationLicenseUsage
} from 'types/schema-generated-types';

export const getDisplayName = (
  product: Product,
  productCurriculum?: ProductCurriculum | null
): string => {
  if (!productCurriculum || productCurriculum.name === product.name) {
    return `${product.code} - ${product.name}`;
  }

  return `${product.code} - ${product.name} - ${productCurriculum.name}`;
};

export const getStudentCurriculumDisplayName = (productCurriculum: ProductCurriculum): string => {
  return `${productCurriculum.name} (${productCurriculum.code})`;
};

export const getId = (product: Product, productCurriculum?: ProductCurriculum | null): string => {
  let productId = `product_${product.code}`;
  if (productCurriculum) productId += `_curriculum_${productCurriculum.code}`;
  return productId;
};

export const parseId = (
  id: string
): { productCode?: string; productCurriculumCode?: string | null } => {
  const result = id.match(/product_(.+)_curriculum_(.+)/);
  return { productCode: result?.[1], productCurriculumCode: result?.[2] };
};

export const getActiveStudentProducts = (
  licenseUsage: StudentLicenseUsage[]
): ProductWithCurriculum[] => {
  const products = licenseUsage.reduce((acc, usage) => {
    if (!usage.active) return acc;

    acc.push({
      product: usage.product,
      ...(usage.productCurriculum && { productCurriculum: usage.productCurriculum })
    });

    return acc;
  }, [] as ProductWithCurriculum[]);

  return uniqBy(products, ({ product }) => product.family.code);
};

export const getAssignedProducts = (
  licenseUsage: ClassroomLicenseUsage[] | StudentLicenseUsage[] | UserLicenseUsage[],
  includeTrial = false
): ProductWithCurriculum[] => {
  return (licenseUsage as (
    | ClassroomLicenseUsage
    | StudentLicenseUsage
    | UserLicenseUsage
  )[]).reduce((acc, usage) => {
    if (!usage.assignedCount) return acc;
    if (!includeTrial && (usage as StudentLicenseUsage | UserLicenseUsage).trial) return acc;

    acc.push({
      product: usage.product,
      ...(usage.productCurriculum && { productCurriculum: usage.productCurriculum })
    });

    return acc;
  }, [] as { product: Product; productCurriculum?: ProductCurriculum }[]);
};

export const sortProductByGrades = (product: Product, otherProduct: Product) => {
  const lowestGradeLevel = product.gradeLevel?.length
    ? [...product.gradeLevel].sort(sortGrade)[0]
    : '';
  const otherLowestGradeLevel = otherProduct.gradeLevel?.length
    ? [...otherProduct.gradeLevel].sort(sortGrade)[0]
    : '';
  const result = sortGrade(lowestGradeLevel, otherLowestGradeLevel);

  if (!result) return product.code.localeCompare(otherProduct.code);

  return result;
};

export const sortProductCurriculumByGrades = (
  productCurriculum: ProductCurriculum,
  otherProductCurriculum: ProductCurriculum
) => {
  const lowestGradeLevel = productCurriculum.gradeLevel?.length
    ? [...productCurriculum.gradeLevel].sort(sortGrade)[0]
    : '';
  const otherLowestGradeLevel = otherProductCurriculum.gradeLevel?.length
    ? [...otherProductCurriculum.gradeLevel].sort(sortGrade)[0]
    : '';
  const result = sortGrade(lowestGradeLevel, otherLowestGradeLevel);

  // Grade levels are the same, fallback to sorting by ID (ascending) - suppose this could be
  // done by name, but using ID puts most recently-created last which I _think_ is what we want for now.
  if (!result) return productCurriculum.id - otherProductCurriculum.id;

  return result;
};

export const getProductNameAndIcon = (
  productFamily: ProductFamily,
  type: ProductType
): { iconUrl?: Maybe<string>; name: string } => {
  const shouldUseProduct = Object.values(ProductType).every(
    value => productFamily.products?.filter(product => product?.type === value)?.length === 1
  );

  if (shouldUseProduct) {
    const product = productFamily.products.find(product => product?.type === type);
    if (product) {
      return { iconUrl: product.iconUrl, name: product.name };
    }
  }

  return { iconUrl: productFamily.iconUrl, name: productFamily.name };
};

export const getProductFamilyIds = (
  usages: OrganizationLicenseUsage[] = [],
  options: {
    productType?: ProductType;
    filterPredicate?: ValueIterateeCustom<OrganizationLicenseUsage, boolean>;
    sortBy?: Many<ValueIteratee<OrganizationLicenseUsage>>;
  } = {}
): number[] =>
  flow(
    ...(options.productType
      ? [filter<OrganizationLicenseUsage>(x => x.product.type === ProductType.STUDENT)]
      : []),
    ...(options.filterPredicate ? [filter(options.filterPredicate)] : []),
    ...(options.sortBy ? [sortBy(options.sortBy)] : []),
    map('product.family.id'),
    uniq
  )(usages);

export const aggregateUsages = (usages: OrganizationLicenseUsage[]) => {
  const result = usages.reduce(
    (acc, usage) => {
      acc.siteLicensed = acc.siteLicensed || usage.siteLicensed;
      acc.assignedCount += usage.assignedCount ?? 0;
      acc.availableCount += usage.availableCount ?? 0;
      acc.totalCount += usage.totalCount ?? 0;
      acc.expiringCount += usage.expiringCount ?? 0;

      if (usage.overageStats) {
        acc.overageExpiringCount += usage.overageStats.expiringCount ?? 0;
        acc.overageTotalCount += usage.overageStats.totalCount ?? 0;
        acc.overageLimitCount += usage.overageStats.overageLimitCount ?? 0;
      }

      return acc;
    },
    {
      siteLicensed: false,
      assignedCount: 0,
      availableCount: 0,
      totalCount: 0,
      expiringCount: 0,
      overageExpiringCount: 0,
      overageTotalCount: 0,
      overageLimitCount: 0
    }
  );

  const {
    siteLicensed,
    assignedCount,
    availableCount,
    totalCount,
    expiringCount,
    overageExpiringCount,
    overageTotalCount,
    overageLimitCount
  } = result;

  // 'Real' refers to actual, non-overage licenses - licenses that the user has purchased
  // 'Potential' considers both the 'real' licenses PLUS any potential overage licenses that can be provisioend.
  const realTotalCount = totalCount - overageTotalCount;
  const potentialTotalCount = realTotalCount + overageLimitCount; // 'overageLimitCount' is the maximum number of overage licenses that can be provisioned.
  const realAvailableCount = realTotalCount - assignedCount;
  const potentialAvailableCount = potentialTotalCount - assignedCount;

  return {
    siteLicensed,
    realAvailableCount,
    realTotalCount,
    assignedCount,
    availableCount,
    expiringCount,
    overageExpiringCount,
    potentialAvailableCount,
    potentialTotalCount
  };
};
